import React, { useState, useRef, useEffect } from 'react';
import { useNavigate }    from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box, Typography, Container, TextField, Button,
    Grid, Stack, Chip, Divider, Menu, MenuItem, Alert,
    CircularProgress, Card, CardContent, Paper, IconButton
} from '@mui/material';
import {
    DirectionsBus, DirectionsWalk, DirectionsBike,
    AddPhotoAlternate as AddPhotoIcon,
    ArrowBack as ArrowBackIcon,
    Rocket as RocketIcon,
    Place as PlaceIcon,
    AccessTime as TimeIcon,
    Group as GroupIcon,
    Flag as FlagIcon,
    LocationOn as StartIcon,
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import axiosInstance       from '../api/axiosConfig';
import { useAuth }         from '../context/AuthContext';
import { useCurrency }     from '../context/CurrencyContext';
import { amountToCents, formatCurrency } from '../utils/currency';

const TERRACOTTA = '#C4622D';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const PARCHMENT  = '#F7F3EE';
const STONE      = '#EDE8E1';
const WALNUT     = '#2C1F14';

const DAY_LABELS   = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const ALL_DAYS     = 127; // bitmask 1111111

function InfoField({ label, icon, name, value, onChange, type='text', placeholder='' }) {
    return (
        <Grid item xs={6} sm={3} textAlign="center">
            <Stack alignItems="center" spacing={0.8}>
                {icon}
                <Typography variant="caption" color="textSecondary" fontWeight={900}
                            sx={{ textTransform:'uppercase', letterSpacing:'0.06em' }}>
                    {label}
                </Typography>
                <TextField size="small" variant="standard" name={name} value={value}
                           onChange={onChange} type={type} placeholder={placeholder || label}
                           inputProps={{ style:{ textAlign:'center', fontWeight:600 } }}
                           sx={{ width:'100%', maxWidth:140 }} />
            </Stack>
        </Grid>
    );
}

// ── Slot row editor ───────────────────────────────────────────────────────────
function SlotRow({ slot, onChange, onDelete }) {
    const toggleDay = (i) => onChange({ ...slot, activeDays: slot.activeDays ^ (1 << i) });
    return (
        <Paper sx={{ p:1.5, border:`1px solid rgba(196,98,45,0.2)`, borderRadius:2 }}>
            <Stack direction={{ xs:'column', sm:'row' }} spacing={1.5} alignItems={{ xs:'flex-start', sm:'center' }} flexWrap="wrap">
                {/* Time inputs */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink:0 }}>
                    <Typography variant="caption" sx={{ minWidth:32, color:'text.secondary' }}>Start</Typography>
                    <input type="time" value={slot.startTime}
                           onChange={e => onChange({ ...slot, startTime:e.target.value })}
                           style={{ border:'1px solid #ccc', borderRadius:4, padding:'3px 6px', fontSize:13 }} />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink:0 }}>
                    <Typography variant="caption" sx={{ minWidth:32, color:'text.secondary' }}>End</Typography>
                    <input type="time" value={slot.endTime}
                           onChange={e => onChange({ ...slot, endTime:e.target.value })}
                           style={{ border:'1px solid #ccc', borderRadius:4, padding:'3px 6px', fontSize:13 }} />
                </Stack>

                {/* Day toggles */}
                <Stack direction="row" spacing={0.4} flexWrap="wrap" gap={0.4} sx={{ flex:1 }}>
                    {DAY_LABELS.map((label, i) => {
                        const active = slot.activeDays & (1 << i);
                        return (
                            <Chip key={label} label={label} size="small" onClick={() => toggleDay(i)}
                                  sx={{ bgcolor:active ? TERRACOTTA:'transparent',
                                      color: active ? 'white':TERRACOTTA,
                                      border:`1px solid ${TERRACOTTA}`, fontWeight:700,
                                      fontSize:'0.62rem', cursor:'pointer', height:22,
                                      '&:hover': { bgcolor: active ? '#9A4A1F':'rgba(196,98,45,0.12)' } }} />
                        );
                    })}
                </Stack>

                <IconButton size="small" color="error" onClick={onDelete} sx={{ flexShrink:0 }}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Stack>
        </Paper>
    );
}

export default function CreateTour() {
    const { user, loading: authLoading } = useAuth();
    const navigate              = useNavigate();
    const { t }                 = useTranslation();
    const { currency }          = useCurrency();
    const fileInputRef = useRef(null);

    // Tour types inside component — labels update on language change
    const TOUR_TYPES = [
        { value:'WALK', label:t('tour.type_walk'), icon:<DirectionsWalk/> },
        { value:'BIKE', label:t('tour.type_bike'), icon:<DirectionsBike/> },
        { value:'BUS',  label:t('tour.type_bus'),  icon:<DirectionsBus/>  },
    ];

    const [form, setForm] = useState({
        title:'', type:'WALK', place:'', duration:'',
        maxGroupSize:'', startPoint:'', endPoint:'', description:'', basePrice:'',
    });
    // Slots created before publishing
    const [slots,        setSlots]        = useState([]);
    const [imageFile,    setImageFile]    = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [iconAnchor,   setIconAnchor]   = useState(null);
    const [submitting,   setSubmitting]   = useState(false);
    const [error,        setError]        = useState('');

    useEffect(() => {
        if (!authLoading && (!user || (user.role !== 'MANAGER' && user.role !== 'ADMIN')))
            navigate('/', { replace:true });
    }, [authLoading, user, navigate]);

    if (authLoading) return null;
    if (!user || (user.role !== 'MANAGER' && user.role !== 'ADMIN')) return null;

    const handleChange    = e => setForm(p => ({...p, [e.target.name]: e.target.value }));
    const handleImagePick = e => { const f = e.target.files[0]; if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }};

    // ── Slot management ───────────────────────────────────────────────────────
    const addSlot = () => setSlots(p => [...p, { startTime:'09:00', endTime:'12:00', activeDays:ALL_DAYS }]);
    const updateSlot  = (i, data) => setSlots(p => p.map((s,j) => j===i ? data : s));
    const removeSlot  = (i)       => setSlots(p => p.filter((_,j) => j!==i));

    const validate = () => {
        if (!form.title.trim())      return t('create_tour.err_title');
        if (!form.place.trim())      return t('create_tour.err_location');
        if (!form.duration.trim())   return t('create_tour.err_duration');
        if (!form.startPoint.trim()) return t('create_tour.err_start');
        if (!form.maxGroupSize || isNaN(Number(form.maxGroupSize)) || Number(form.maxGroupSize) < 1)
            return t('create_tour.err_group');
        if (!form.basePrice || isNaN(Number(form.basePrice)) || Number(form.basePrice) < 0)
            return t('create_tour.err_price');
        if (!imageFile) return t('create_tour.err_image');
        return null;
    };

    const handleSubmit = async () => {
        const err = validate();
        if (err) { setError(err); return; }
        setSubmitting(true); setError('');
        try {
            // 1. Create tour offer
            const payload = {
                title:form.title.trim(), description:form.description.trim(),
                place:form.place.trim(), duration:form.duration.trim(),
                maxGroupSize:Number(form.maxGroupSize),
                startPoint:form.startPoint.trim(),
                endPoint:form.endPoint.trim() || null,
                basePrice: amountToCents(Number(form.basePrice), currency),
                currency,
                type:form.type, appUserId:user.id,
            };
            const data = new FormData();
            data.append('tourOffer', new Blob([JSON.stringify(payload)], { type:'application/json' }));
            data.append('image', imageFile);
            const res = await axiosInstance.post('/api/tourOffers/tourOffer', data, {
                headers: { 'Content-Type':'multipart/form-data' }
            });
            const tourId = res.data.id;

            // 2. Create slots (if any defined before publishing)
            await Promise.all(slots.map(slot =>
                axiosInstance.post('/tour_slots/tour_slot', {
                    tourOfferId: tourId, tourOffer: { id: tourId },
                    startTime:   slot.startTime.length===5 ? `${slot.startTime}:00` : slot.startTime,
                    endTime:     slot.endTime.length===5   ? `${slot.endTime}:00`   : slot.endTime,
                    activeDays:  slot.activeDays,
                })
            ));

            navigate(`/tours/${tourId}`);
        } catch (e) {
            setError(e.response?.data?.message || t('create_tour.err_generic'));
            setSubmitting(false);
        }
    };

    const selectedTypeLabel = TOUR_TYPES.find(t => t.value === form.type)?.label || 'Tour';

    const previewFacts = [
        { label:t('create_tour.preview_title_label'), val:form.title       || '—' },
        { label:t('create_tour.preview_location'),    val:form.place       || '—' },
        { label:t('create_tour.preview_duration'),    val:form.duration    || '—' },
        { label:t('create_tour.preview_max_group'),   val:form.maxGroupSize ? `${form.maxGroupSize} pax`:'—' },
        { label:t('create_tour.preview_start'),       val:form.startPoint  || '—' },
        { label:t('create_tour.preview_end'),         val:form.endPoint    || '—' },
    ];

    const checklistItems = [
        { label:t('create_tour.check_title'),    done:!!form.title.trim() },
        { label:t('create_tour.check_location'), done:!!form.place.trim() },
        { label:t('create_tour.check_duration'), done:!!form.duration.trim() },
        { label:t('create_tour.check_start'),    done:!!form.startPoint.trim() },
        { label:t('create_tour.check_group'),    done:!!form.maxGroupSize && !isNaN(Number(form.maxGroupSize)) },
        { label:t('create_tour.check_price'),    done:!!form.basePrice && !isNaN(Number(form.basePrice)) },
        { label:t('create_tour.check_image'),    done:!!imageFile },
    ];

    const TYPE_ICON_SX = { fontSize:40, color:TERRACOTTA };
    const renderTypeIcon = t => {
        switch(t) {
            case 'BUS':  return <DirectionsBus  sx={TYPE_ICON_SX}/>;
            case 'WALK': return <DirectionsWalk sx={TYPE_ICON_SX}/>;
            case 'BIKE': return <DirectionsBike sx={TYPE_ICON_SX}/>;
            default:     return <DirectionsBus  sx={TYPE_ICON_SX}/>;
        }
    };

    return (
        <Box sx={{ position:'relative', minHeight:'100vh', bgcolor:PARCHMENT }}>
            <Box sx={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
                width:'100%', height:'100%', opacity:0.04, zIndex:0, pointerEvents:'none',
                backgroundImage:`url('https://i.assetzen.net/i/P6kr9CyK1FUZ/w:auto/h:400/q:70.jpg')`,
                backgroundRepeat:'no-repeat', backgroundPosition:'center', backgroundSize:'contain' }} />

            <Container maxWidth="xl" sx={{ py:4, position:'relative', zIndex:1 }}>

                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between"
                       flexWrap="wrap" gap={2} mb={4}>
                    <Button startIcon={<ArrowBackIcon/>} onClick={() => navigate(-1)}
                            sx={{ color:TERRACOTTA, fontWeight:'bold' }}>
                        {t('tour.back')}
                    </Button>
                    <Box>
                        <Typography variant="overline" sx={{ color:FOREST, fontWeight:700, letterSpacing:'0.2em' }}>
                            {t('create_tour.page_overline')}
                        </Typography>
                        <Typography variant="h4" fontWeight={900} sx={{ color:WALNUT, lineHeight:1.1 }}>
                            {t('create_tour.title')}
                        </Typography>
                    </Box>
                    <Button variant="contained" size="large"
                            startIcon={submitting ? <CircularProgress size={18} color="inherit"/> : <RocketIcon/>}
                            onClick={handleSubmit} disabled={submitting}
                            sx={{ background:`linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                color:'white', borderRadius:2, fontWeight:700, px:3,
                                '&:hover': { background:`linear-gradient(135deg, #D4723D 0%, #AA5A2F 100%)` } }}>
                        {submitting ? t('create_tour.publishing') : t('create_tour.publish')}
                    </Button>
                </Stack>

                {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb:3, borderRadius:2 }}>{error}</Alert>}

                <Grid container spacing={5}>
                    {/* LEFT COLUMN */}
                    <Grid item xs={12} md={8}>

                        {/* Type + Title */}
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <Box onClick={e => setIconAnchor(e.currentTarget)}
                                 sx={{ cursor:'pointer', borderRadius:2, p:0.5, transition:'0.2s',
                                     '&:hover': { bgcolor:'rgba(196,98,45,0.08)', outline:`1px dashed ${TERRACOTTA}` } }}>
                                {renderTypeIcon(form.type)}
                            </Box>
                            <Menu anchorEl={iconAnchor} open={Boolean(iconAnchor)} onClose={() => setIconAnchor(null)}>
                                {TOUR_TYPES.map(tt => (
                                    <MenuItem key={tt.value} onClick={() => { setForm(p=>({...p,type:tt.value})); setIconAnchor(null); }}>
                                        <Box sx={{ mr:1, color:TERRACOTTA, display:'flex' }}>{tt.icon}</Box>{tt.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                            <Box sx={{ flexGrow:1 }}>
                                <TextField fullWidth name="title" value={form.title} onChange={handleChange}
                                           placeholder={t('create_tour.title_placeholder')} variant="standard"
                                           inputProps={{ style:{ fontSize:32, fontWeight:800, color:WALNUT } }}
                                           sx={{ '& .MuiInput-underline:before': { borderBottomColor:'rgba(196,98,45,0.2)' },
                                               '& .MuiInput-underline:after':  { borderBottomColor:TERRACOTTA } }} />
                                <Chip label={selectedTypeLabel} size="small" variant="outlined"
                                      sx={{ mt:0.8, borderColor:'rgba(196,98,45,0.3)', color:TERRACOTTA, fontWeight:700 }} />
                            </Box>
                        </Stack>

                        {/* Cover image */}
                        <Box onClick={() => fileInputRef.current?.click()}
                             sx={{ position:'relative', mb:4, width:'100%', maxWidth:800, height:400,
                                 borderRadius:2, overflow:'hidden',
                                 border:imagePreview ? `3px solid ${STONE}` : '3px dashed rgba(196,98,45,0.35)',
                                 bgcolor:imagePreview ? 'transparent':'rgba(247,243,238,0.8)',
                                 backgroundImage:imagePreview ? `url(${imagePreview})`:'none',
                                 backgroundSize:'cover', backgroundPosition:'center',
                                 cursor:'pointer', transition:'border-color 0.2s',
                                 display:'flex', alignItems:'center', justifyContent:'center',
                                 '&:hover': { borderColor:TERRACOTTA },
                                 '&:hover .upload-overlay': { opacity:1 } }}>
                            <Box className="upload-overlay"
                                 sx={{ position:'absolute', inset:0,
                                     bgcolor:imagePreview ? 'rgba(196,98,45,0.45)':'transparent',
                                     display:'flex', flexDirection:'column',
                                     alignItems:'center', justifyContent:'center',
                                     opacity:imagePreview ? 0:1, transition:'0.3s' }}>
                                <AddPhotoIcon sx={{ fontSize:56, color:imagePreview ? 'white':'rgba(196,98,45,0.5)', mb:1 }}/>
                                <Typography variant="h6" fontWeight={700} sx={{ color:imagePreview ? 'white':TERRACOTTA }}>
                                    {imagePreview ? t('create_tour.change_image') : t('create_tour.upload_image')}
                                </Typography>
                                <Typography variant="caption" sx={{ color:imagePreview ? 'rgba(255,255,255,0.75)':'text.secondary', mt:0.5 }}>
                                    {imagePreview ? t('create_tour.image_replace') : t('create_tour.image_hint')}
                                </Typography>
                            </Box>
                        </Box>
                        <input ref={fileInputRef} type="file" hidden accept="image/*" onChange={handleImagePick}/>

                        {/* Quick info grid */}
                        <Grid container spacing={2} sx={{ mb:4, bgcolor:'#FFF', p:3, borderRadius:2,
                            maxWidth:800, borderLeft:`6px solid ${TERRACOTTA}`, boxShadow:'0 2px 12px rgba(44,31,20,0.06)' }}>
                            <InfoField label={t('create_tour.duration_label')}    name="duration"     value={form.duration}     onChange={handleChange} placeholder={t('create_tour.duration_placeholder')}    icon={<TimeIcon  sx={{ color:TERRACOTTA }}/>} />
                            <InfoField label={t('create_tour.group_size_label')}  name="maxGroupSize" value={form.maxGroupSize} onChange={handleChange} placeholder={t('create_tour.group_size_placeholder')} type="number" icon={<GroupIcon sx={{ color:FOREST }}/>} />
                            <InfoField label={t('create_tour.start_point_label')} name="startPoint"   value={form.startPoint}   onChange={handleChange} placeholder={t('create_tour.start_point_placeholder')} icon={<StartIcon sx={{ color:TERRACOTTA }}/>} />
                            <InfoField label={t('create_tour.end_point_label')}   name="endPoint"     value={form.endPoint}     onChange={handleChange} placeholder={t('create_tour.end_point_placeholder')}   icon={<FlagIcon  sx={{ color:FOREST }}/>} />
                        </Grid>

                        {/* Location */}
                        <Box sx={{ mb:3, maxWidth:800 }}>
                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <PlaceIcon sx={{ color:TERRACOTTA, fontSize:'1.1rem' }}/>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ color:WALNUT }}>{t('create_tour.location_label')}</Typography>
                            </Stack>
                            <TextField fullWidth name="place" value={form.place} onChange={handleChange}
                                       placeholder={t('create_tour.location_placeholder')}
                                       variant="outlined" size="small" sx={{ maxWidth:400 }}/>
                        </Box>

                        {/* Base price */}
                        <Box sx={{ mb:4, maxWidth:800 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ color:WALNUT, mb:1 }}>
                                {t('create_tour.base_price_label')}
                            </Typography>
                            <TextField name="basePrice" value={form.basePrice} onChange={handleChange}
                                       placeholder="0.00" variant="outlined" size="small" type="number"
                                       inputProps={{ min:0, step:0.01 }} sx={{ width:160 }}
                                       InputProps={{ startAdornment:<Typography sx={{ mr:0.5, color:'text.secondary' }}>$</Typography> }}/>
                        </Box>

                        {/* Description */}
                        <Box sx={{ mb:5, maxWidth:800 }}>
                            <Typography variant="h5" fontWeight="bold" sx={{ color:TERRACOTTA, mb:2 }}>
                                {t('create_tour.about_label')}
                            </Typography>
                            <TextField fullWidth multiline rows={10} name="description"
                                       value={form.description} onChange={handleChange}
                                       placeholder={t('create_tour.desc_placeholder')} variant="outlined"
                                       sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor:TERRACOTTA } } }}/>
                        </Box>

                        {/* ── TIME SLOTS EDITOR ──────────────────────────────── */}
                        <Box sx={{ mb:5, maxWidth:800 }}>
                            <Typography variant="h5" fontWeight="bold" sx={{ color:WALNUT, mb:0.5 }}>
                                Time Slots
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb:2 }}>
                                Define what time this tour runs and on which days of the week. You can add or edit more after publishing.
                            </Typography>

                            <Stack spacing={1.5} sx={{ mb:2 }}>
                                {slots.map((slot, i) => (
                                    <SlotRow key={i} slot={slot}
                                             onChange={data => updateSlot(i, data)}
                                             onDelete={() => removeSlot(i)}/>
                                ))}
                            </Stack>

                            <Button size="small" startIcon={<AddIcon/>} variant="outlined"
                                    sx={{ color:TERRACOTTA, borderColor:TERRACOTTA }}
                                    onClick={addSlot}>
                                Add Time Slot
                            </Button>

                            {slots.length === 0 && (
                                <Box sx={{ mt:2, p:2.5, borderRadius:2, textAlign:'center',
                                    border:'2px dashed rgba(196,98,45,0.25)', bgcolor:'rgba(247,243,238,0.6)' }}>
                                    <TimeIcon sx={{ fontSize:32, color:'rgba(196,98,45,0.35)', mb:0.5 }}/>
                                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                        {t('create_tour.itinerary_note')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('create_tour.itinerary_sub')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                    </Grid>

                    {/* RIGHT COLUMN — Preview */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ position:'sticky', top:100 }}>
                            <Card elevation={0} sx={{ borderRadius:3, border:'1px solid rgba(196,98,45,0.18)',
                                bgcolor:'#FFF', boxShadow:'0 8px 32px rgba(196,98,45,0.08)' }}>
                                <CardContent sx={{ p:4 }}>
                                    <Stack spacing={2.5}>
                                        {/* Checklist */}
                                        <Box>
                                            <Typography variant="caption" color="text.secondary"
                                                        sx={{ fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', display:'block', mb:1.5 }}>
                                                {t('create_tour.checklist_title')}
                                            </Typography>
                                            {checklistItems.map(({label,done}) => (
                                                <Box key={label} sx={{ display:'flex', alignItems:'center', gap:1, mb:0.8 }}>
                                                    <Box sx={{ width:18, height:18, borderRadius:'50%', flexShrink:0,
                                                        bgcolor:done ? FOREST:'rgba(0,0,0,0.08)',
                                                        display:'flex', alignItems:'center', justifyContent:'center', transition:'background 0.25s' }}>
                                                        {done && <Box component="span" sx={{ width:6, height:6, borderRadius:'50%', bgcolor:'white' }}/>}
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color:done ? WALNUT:'text.disabled', fontWeight:done?600:400 }}>
                                                        {label}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>

                                        <Button fullWidth variant="contained" size="large"
                                                startIcon={submitting ? <CircularProgress size={18} color="inherit"/> : <RocketIcon/>}
                                                onClick={handleSubmit} disabled={submitting}
                                                sx={{ mt:1, py:1.6, fontWeight:900, fontSize:'1rem',
                                                    background:`linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                                    color:'white', borderRadius:2,
                                                    '&:hover': { background:`linear-gradient(135deg, #D4723D 0%, #AA5A2F 100%)` },
                                                    '&.Mui-disabled': { bgcolor:'rgba(0,0,0,0.1)', color:'rgba(0,0,0,0.3)' } }}>
                                            {submitting ? t('create_tour.publishing') : t('create_tour.publish')}
                                        </Button>
                                        <Typography variant="caption" color="text.secondary" textAlign="center">
                                            {t('create_tour.after_publish')}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}